import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetCompanyEntities } from 'src/shared/queries/useGetCompanyEntities';
import { useGetQuestionnaireAnswers } from 'src/shared/queries/useGetQuestionnaireAnswers';
import { useGetQuestionnaireDetails } from 'src/shared/queries/useGetQuestionnaireDetails';
import { useGetQuestionnaireDetailsTree } from 'src/shared/queries/useGetQuestionnaireDetailsTree';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { Button, Loader } from '@ds';
import {
  QuestionnairePrintViewService,
  IQuestionListIdMap,
  IPrintCategory,
  IPrintQuestion,
  IQuestionnaireIdMap,
  IQuestionRowProps,
  sanitizeHtml,
  InputFieldConfiguration,
  GroupedTypes,
} from './QuestionnairePrintView.service';
import { FallbackPlaceholderText } from 'src/utils/constants/common';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import UserIcon from 'src/components/UserIcon/UserIcon';
import { UserIconType } from 'src/utils/constants/User';
import FullNameTemplate from './templates/FullNameTemplate';
import AddressTemplate from './templates/AddressTemplate';
import CountryStateTemplate from './templates/CountryStateTemplate';
import EmailAddressTemplate from './templates/EmailAddressTemplate';
import PhoneNumberTemplate from './templates/PhoneNumberTemplate';
import TypeSpecificWorkLocationTemplate from './templates/TypeSpecificWorkLocationTemplate';
import WorkLocationTemplate from './templates/WorkLocationTemplate';
import styles from './QuestionnairePrintView.module.scss';
import { DateFormat, getFormattedDate } from 'src/utils/formatters/formatDate';

const renderAddressTemplate = (answerGroups: { answerMapping: Record<string, any> }[]) => {
  return <AddressTemplate answerGroups={answerGroups} />;
};

const renderFullNameTemplate = (answerMapping: Record<string, any>) => {
  return <FullNameTemplate answerMapping={answerMapping} />;
};

const renderCountryStateTemplate = (answerMapping: Record<string, any>) => {
  return <CountryStateTemplate answerMapping={answerMapping} />;
};

const renderEmailAddressTemplate = (answerGroups: { answerMapping: Record<string, any> }[]) => {
  return <EmailAddressTemplate answerGroups={answerGroups} />;
};

const renderPhoneNumberTemplate = (answerGroups: { answerMapping: Record<string, any> }[]) => {
  return <PhoneNumberTemplate answerGroups={answerGroups} />;
};

const renderTypeSpecificWorkLocationTemplate = (answerGroups: { answerMapping: Record<string, any> }[]) => {
  return <TypeSpecificWorkLocationTemplate answerGroups={answerGroups} />;
};

const renderWorkLocationTemplate = (answerGroups: { answerMapping: Record<string, any> }[]) => {
  return <WorkLocationTemplate answerGroups={answerGroups} />;
};

const QuestionRow: React.FC<IQuestionRowProps> = ({ question, subQuestionLevel = 0 }) => {
  const { data: userInfo } = useGetUser();
  const companyId = userInfo?.companyId ?? 0;
  const { data: companyEntities } = useGetCompanyEntities(companyId);

  const renderGroupTypeByTemplate = (
    question: IPrintQuestion,
    answerMapping: Record<string, any>,
    answerGroups?: { answerMapping: Record<string, any> }[]
  ) => {
    // Dynamic template rendering based on questionInputField
    const templateType = question.questionInputField === 'ADDRESS_MULTI' ? 'ADDRESS' : question.questionInputField;
    switch (templateType) {
      case 'FULL_NAME':
        return renderFullNameTemplate(answerMapping);
      case 'ADDRESS':
        return renderAddressTemplate(answerGroups || []);
      case 'COUNTRY_STATE':
        return renderCountryStateTemplate(answerMapping);
      case 'EMAIL_ADDRESS':
        return renderEmailAddressTemplate(answerGroups || []);
      case 'PHONE_NUMBER':
        return renderPhoneNumberTemplate(answerGroups || []);
      case 'TYPE_SPECIFIC_WORK_LOCATION':
        return renderTypeSpecificWorkLocationTemplate(answerGroups || []);
      case 'WORK_LOCATION':
        return renderWorkLocationTemplate(answerGroups || []);
      default:
        return (
          <div className={styles['group-type-answer']}>
            {Object.entries(answerMapping).map(([index, answer]: [string, any]) => (
              <div key={index} style={{ marginBottom: '3px' }}>
                <strong>Field {parseInt(index) + 1}:</strong>{' '}
                {answer.answerForQuestion || FallbackPlaceholderText.TripleDash}
              </div>
            ))}
          </div>
        );
    }
  };

  const renderGroupTypeAnswers = (question: IPrintQuestion) => {
    const config = InputFieldConfiguration[question.questionInputField];
    if (!config) {
      return <p>Unsupported group type: {question.questionInputField}</p>;
    }

    const numberOfAnswers = config.numberOfFields;
    const isGroupedType = GroupedTypes.includes(question.questionInputField);

    if (isGroupedType) {
      const answerGroups = QuestionnairePrintViewService.getAnswerGroups(
        question.answers,
        numberOfAnswers,
        question.questionInputField
      );
      return renderGroupTypeByTemplate(question, answerGroups[0]?.answerMapping || {}, answerGroups);
    } else {
      const answerMapping = QuestionnairePrintViewService.getAnswerMapping(
        question.answers,
        numberOfAnswers,
        question.questionInputField
      );
      return renderGroupTypeByTemplate(question, answerMapping);
    }
  };

  return (
    <tr className={styles['question-item']}>
      <td className={`${styles['question-col-no']} ${styles[`sub-level-${subQuestionLevel}`]}`}>
        {question.questionNo?.join('.')}
      </td>
      <td className={styles['question-col-qna']}>{question.question}</td>
      <td className={styles['question-col-qnanwers']}>
        {QuestionnairePrintViewService.checkEmptyAnswer(question) ? (
          <p>{FallbackPlaceholderText.TripleDash}</p>
        ) : (
          <>
            {QuestionnairePrintViewService.isGroupType(question.questionInputField) ? (
              renderGroupTypeAnswers(question)
            ) : (
              <ul>
                {question.answers.map((answer, answerIndex: number) => (
                  <li key={answerIndex}>
                    {!answer.answerId &&
                      !QuestionnairePrintViewService.isDateType(question.questionInputField) &&
                      !QuestionnairePrintViewService.isCountryType(question.questionInputField) &&
                      !QuestionnairePrintViewService.isEntityType(question.questionInputField) && (
                        <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer.answerForQuestion || '') }} />
                      )}
                    {answer.answerId && (
                      <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer.answerOption || '') }} />
                    )}
                    {QuestionnairePrintViewService.isDateType(question.questionInputField) && (
                      <p>
                        {getFormattedDate(answer.answerForQuestion, userInfo?.userSettings?.dateFormat as DateFormat)}
                      </p>
                    )}
                    {QuestionnairePrintViewService.isCountryType(question.questionInputField) && (
                      <p>{answer.answerForQuestion || ''}</p>
                    )}
                    {QuestionnairePrintViewService.isEntityType(question.questionInputField) && (
                      <p>
                        {QuestionnairePrintViewService.getEntityName(
                          answer.answerForQuestion || '',
                          companyEntities || []
                        )}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

const QuestionnairePrintView: React.FC = () => {
  const params = useParams<{ questionnaireId?: string; caseQuestionnaireIdList?: string }>();
  const { data: countries } = useGetCountries();
  const caseQuestionnaireIdList = useMemo(() => {
    return QuestionnairePrintViewService.parseQuestionnaireIdList(params);
  }, [params]);
  const [loading, setLoading] = useState(true);
  const [questionListIdMap, setQuestionListIdMap] = useState<IQuestionListIdMap>({});
  const [questionnaireIdMap, setQuestionnaireIdMap] = useState<IQuestionnaireIdMap>({});
  const firstQuestionnaireId = caseQuestionnaireIdList[0];
  const { data: questionnaireDetails } = useGetQuestionnaireDetails(firstQuestionnaireId || 0);
  const { data: questionnaireDetailsTree } = useGetQuestionnaireDetailsTree(
    questionnaireDetails?.questionnaireId || 0,
    firstQuestionnaireId || 0
  );
  const { data: questionnaireAnswers } = useGetQuestionnaireAnswers(firstQuestionnaireId || 0);

  useEffect(() => {
    if (!questionnaireDetailsTree || !questionnaireAnswers || !questionnaireDetails) {
      return;
    }

    try {
      setLoading(true);

      const processedQuestions = QuestionnairePrintViewService.processQuestionnaireData(
        JSON.parse(JSON.stringify(questionnaireDetailsTree)),
        questionnaireAnswers
      );

      setQuestionListIdMap((prev) => ({ ...prev, [firstQuestionnaireId]: processedQuestions }));
      setQuestionnaireIdMap((prev) => ({ ...prev, [firstQuestionnaireId]: questionnaireDetails }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [questionnaireDetailsTree, questionnaireAnswers, questionnaireDetails, firstQuestionnaireId]);

  const destinationCountry = useMemo(() => {
    return (
      countries?.find((country) => country.countryCode === questionnaireDetails?.destinationCountry)?.countryName ||
      FallbackPlaceholderText.TripleDash
    );
  }, [countries, questionnaireDetails?.destinationCountry]);

  return (
    <div className={styles['questionnaire-print-view-container']}>
      {loading || !questionnaireDetails ? (
        <div className={styles['loader-container']}>
          <Loader />
        </div>
      ) : (
        <div className={styles['questionnaire-details']}>
          <div className={styles['print-button-container']}>
            <Button onClick={() => window.print()}>Print</Button>
          </div>

          <table className={styles['print-table']}>
            <thead>
              <tr>
                <th>
                  <h1 className={styles['questionnaire-title']}>{questionnaireDetails.questionnaire}</h1>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className={styles['general-info-container']}>
                    <table>
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Questionnaire Recipient</th>
                          <th>Primary Attorney</th>
                          <th>Destination Country/Location</th>
                          <th>Case Subtype(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{questionnaireDetails.companyName}</td>
                          <td>{questionnaireDetails.recipient}</td>
                          <td>{questionnaireDetails.primaryAttorneyName || FallbackPlaceholderText.TripleDash}</td>
                          <td>{destinationCountry}</td>
                          <td>
                            {questionnaireDetails.caseSubTypes?.length === 0 ? (
                              FallbackPlaceholderText.TripleDash
                            ) : (
                              <div>
                                {questionnaireDetails.caseSubTypes?.map((subtype: string, index: number) => (
                                  <p key={index}>{subtype}</p>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>

                      <thead>
                        <tr>
                          <th>BAL Number</th>
                          <th>&nbsp;</th>
                          <th>Primary Assistant</th>
                          <th>Case Type</th>
                          <th>&nbsp;</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{questionnaireDetails.balNumber || FallbackPlaceholderText.TripleDash}</td>
                          <td>&nbsp;</td>
                          <td>{questionnaireDetails.primaryAssistantName || FallbackPlaceholderText.TripleDash}</td>
                          <td>{questionnaireDetails.caseType || FallbackPlaceholderText.TripleDash}</td>
                          <td>&nbsp;</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div>
                    {caseQuestionnaireIdList.map((caseQuestionnaireId: number, index: number) => {
                      const questionnaireInfo = questionnaireIdMap[caseQuestionnaireId];
                      const questionList = questionListIdMap[caseQuestionnaireId];

                      if (!questionnaireInfo || !questionList) {
                        return null;
                      }

                      return (
                        <div key={index}>
                          <div className={styles['questionnaire-member-container']}>
                            <UserIcon className={styles['user-icon']} userIcon={UserIconType.SINGLE} />
                            <span className={styles['member-title']}>
                              {questionnaireInfo.beneficiaryFullName}
                              {questionnaireInfo.relationship !== 'Employer' && (
                                <span>&nbsp;({questionnaireInfo.relationship})</span>
                              )}
                            </span>
                          </div>

                          {questionList.map((category: IPrintCategory, categoryIndex: number) => (
                            <div key={categoryIndex} className={styles['information-card']}>
                              <div className={styles['questionnaire-section']}>
                                <h4>{category.questionCategory}</h4>

                                <p className={styles['questionnaire-help-text']}>
                                  {typeof category.helpText === 'string'
                                    ? category.helpText
                                    : category.helpText?.categoryHelpText}
                                </p>
                              </div>

                              {category.questions.map((question: IPrintQuestion, questionIndex: number) => (
                                <table key={questionIndex} className={`${styles['table']} ${styles['table-bordered']}`}>
                                  {(question.title || question.helpText) && (
                                    <tbody>
                                      <tr className={styles['no-border']}>
                                        <td colSpan={3}>
                                          <div className={styles['sub-section-header']}>
                                            <p className={styles['sub-section-title']}>{question.title}</p>
                                            <p className={styles['sub-section-help-text']}>{question.helpText}</p>
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  )}

                                  {!question.isSection ? (
                                    <tbody>
                                      <QuestionRow question={question} subQuestionLevel={category.subQuestionLevel} />
                                    </tbody>
                                  ) : (
                                    question.sections
                                      ?.reverse()
                                      .map((subQuestions: IPrintQuestion[], sectionIndex: number) => (
                                        <tbody key={sectionIndex}>
                                          {subQuestions.map((subQuestion: IPrintQuestion, subQuestionIndex: number) => (
                                            <QuestionRow
                                              key={subQuestionIndex}
                                              question={subQuestion}
                                              subQuestionLevel={category.subQuestionLevel}
                                            />
                                          ))}
                                        </tbody>
                                      ))
                                  )}
                                </table>
                              ))}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QuestionnairePrintView;
